import { useState, useEffect } from 'react';
import { authAPI } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Save, Loader2, User, Lock, Mail } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user, checkAuth } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  const handleSaveProfile = async () => {
    if (!email.trim()) { toast.error('E-posta zorunlu'); return; }
    setSavingProfile(true);
    try {
      await authAPI.updateProfile({ name, email });
      toast.success('Profil guncellendi. Yeniden giris yapmaniz gerekebilir.');
      checkAuth();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Profil guncellenemedi');
    } finally { setSavingProfile(false); }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) { toast.error('Mevcut sifre gerekli'); return; }
    if (!newPassword) { toast.error('Yeni sifre gerekli'); return; }
    if (newPassword.length < 6) { toast.error('Yeni sifre en az 6 karakter olmali'); return; }
    if (newPassword !== confirmPassword) { toast.error('Yeni sifreler eslesmiyor'); return; }
    
    setSavingPassword(true);
    try {
      await authAPI.updateProfile({ current_password: currentPassword, new_password: newPassword });
      toast.success('Sifre basariyla degistirildi');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sifre degistirilemedi');
    } finally { setSavingPassword(false); }
  };

  return (
    <div data-testid="admin-profile">
      <h1 className="text-2xl font-semibold mb-6">Profil Ayarlari</h1>

      {/* Profile Info */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><User className="h-5 w-5" /> Hesap Bilgileri</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="profile-name">Ad Soyad</Label>
            <Input
              id="profile-name"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Adiniz"
              data-testid="profile-name"
            />
          </div>
          <div>
            <Label htmlFor="profile-email">E-posta</Label>
            <Input
              id="profile-email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="ornek@uretimnoktasi.com"
              data-testid="profile-email"
            />
          </div>
          <div className="text-sm text-muted-foreground">
            Rol: <span className="font-medium capitalize">{user?.role}</span>
          </div>
          <Button onClick={handleSaveProfile} disabled={savingProfile} data-testid="save-profile">
            {savingProfile ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Profili Kaydet
          </Button>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Lock className="h-5 w-5" /> Sifre Degistir</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="current-pw">Mevcut Sifre</Label>
            <Input
              id="current-pw"
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              placeholder="Mevcut sifreniz"
              data-testid="current-password"
            />
          </div>
          <div>
            <Label htmlFor="new-pw">Yeni Sifre</Label>
            <Input
              id="new-pw"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              placeholder="En az 6 karakter"
              data-testid="new-password"
            />
          </div>
          <div>
            <Label htmlFor="confirm-pw">Yeni Sifre (Tekrar)</Label>
            <Input
              id="confirm-pw"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Yeni sifreyi tekrar girin"
              data-testid="confirm-password"
            />
          </div>
          <Button onClick={handleChangePassword} disabled={savingPassword} data-testid="change-password">
            {savingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
            Sifreyi Degistir
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
